// src/pages/Map.js
import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import './Map.css';

// --- Leaflet Icon Setup ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// --- Helper component to recenter the map ---
function LocationSetter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
}

// --- Helper function to calculate offset positions ---
const calculateSpiderfyPositions = (center, count, distance) => {
  if (count === 1) return [center];
  const positions = [];
  const angleStep = (2 * Math.PI) / count;
  for (let i = 0; i < count; i++) {
    const angle = i * angleStep;
    positions.push([
      center[0] + distance * Math.cos(angle),
      center[1] + distance * Math.sin(angle),
    ]);
  }
  return positions;
};

const Map = () => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    // --- Geolocation and Data Fetching ---
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => setUserLocation([position.coords.latitude, position.coords.longitude]),
        (error) => {
          console.error("Geolocation error:", error);
          setUserLocation([40.7608, -111.8910]); // Fallback to SLC
        }
      );
    } else {
      setUserLocation([40.7608, -111.8910]); // Fallback for browsers
    }
    
    const fetchResourcesAndCategories = async () => {
      setLoading(true);
      try {
        const resourcesSnapshot = await getDocs(collection(db, 'resources'));
        const resourcesData = resourcesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setResources(resourcesData);

        const categoriesSnapshot = await getDocs(collection(db, 'categories'));
        const categoriesData = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCategories(categoriesData);
      } catch (error) { console.error("Error fetching data: ", error); }
      setLoading(false);
    };
    fetchResourcesAndCategories();
  }, []);

  const handleCategoryClick = (categoryId) => setSelectedCategory(categoryId);

  // --- 1. Unpack resources into individual service pins ---
  const servicePins = useMemo(() => {
    const flattened = [];
    resources.forEach(resource => {
      if (resource.categoryIDs && resource.categoryIDs.length > 0) {
        resource.categoryIDs.forEach(catId => {
          flattened.push({
            ...resource,
            categoryID: catId, 
            uniqueId: `${resource.id}-${catId}`,
          });
        });
      }
    });
    return flattened;
  }, [resources]);

  // --- 2. Filter the individual service pins ---
  const filteredPins = useMemo(() => {
    return selectedCategory
      ? servicePins.filter(pin => pin.categoryID === selectedCategory)
      : servicePins;
  }, [servicePins, selectedCategory]);

  // --- 3. Spiderfy the filtered pins ---
  const spiderfiedPins = useMemo(() => {
    const coordinateMap = new window.Map();
    filteredPins.forEach(pin => {
      if (pin.coordinates?.latitude && pin.coordinates?.longitude) {
        const key = `${pin.coordinates.latitude},${pin.coordinates.longitude}`;
        if (!coordinateMap.has(key)) {
          coordinateMap.set(key, []);
        }
        coordinateMap.get(key).push(pin);
      }
    });

    const processedPins = [];
    const SPIDERFY_DISTANCE = 0.00015; // Adjusted for better visibility

    coordinateMap.forEach((group) => {
      const center = [group[0].coordinates.latitude, group[0].coordinates.longitude];
      const positions = calculateSpiderfyPositions(center, group.length, SPIDERFY_DISTANCE);
      group.forEach((pin, index) => {
        processedPins.push({ ...pin, displayPosition: positions[index] });
      });
    });
    
    return processedPins;
  }, [filteredPins]);

  if (loading || !userLocation) return <div>Loading map...</div>;

  return (
    <div className="map-page-container">
      {/* --- Filter Panel --- */}
      <div className="filter-panel">
        <h3 className="filter-title">Filter by Category:</h3>
        <div className="filter-buttons-container">
          <button
            onClick={() => handleCategoryClick('')}
            className={selectedCategory === '' ? 'filter-button active' : 'filter-button'}
          >
            All
          </button>
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className={selectedCategory === category.id ? 'filter-button active' : 'filter-button'}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <MapContainer center={userLocation} zoom={13} className="map-view" zoomControl={false}>
        <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" attribution='© OpenStreetMap © CARTO' />
        <ZoomControl position="topright" />
        <LocationSetter center={userLocation} />

        {spiderfiedPins.map(pin => {
          const position = pin.displayPosition;
          if (!position) return null;

          const categoryForIcon = categories.find(cat => cat.id === pin.categoryID);
          if (!categoryForIcon?.iconUrl) return null;

          const customIcon = new L.Icon({
            iconUrl: categoryForIcon.iconUrl,
            iconAnchor: [18, 35],
            popupAnchor: [0, -35],
            className: 'custom-leaflet-icon'
          });

          // For the popup, we list ALL services for the original resource
          const allServicesForPopup = categories.filter(cat => pin.categoryIDs?.includes(cat.id));
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLocation[0]},${userLocation[1]}&destination=${pin.coordinates.latitude},${pin.coordinates.longitude}`;

          return (
            <Marker key={pin.uniqueId} position={position} icon={customIcon}>
              <Popup>
                <div className="popup-content">
                  <div className="popup-header">
                    <strong>{pin.organizationName || pin.representativeName}</strong>
                    <p>{pin.location}</p>
                  </div>
                  <div className="popup-services">
                    <p className="services-title">Services Offered:</p>
                    {allServicesForPopup.map(cat => (
                      <div key={cat.id} className="service-item">
                        <img src={cat.iconUrl} alt={cat.name} className="service-icon" />
                        <span>{cat.flagMessage}</span>
                      </div>
                    ))}
                  </div>
                  <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="directions-button">
                    Get Directions
                  </a>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default Map;